import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { StructuredAnalysis } from "@/schemas/structured-analysis.schema";
import { useQueryCache } from "@pinia/colada";
import { computed, defineComponent, ref } from "vue";
import { agentsApi } from "@/api/agents.api";
import { knowledgeApi } from "@/api/knowledge.api";
import { secretsApi } from "@/api/secrets.api";
import { structuredAnalysisApi } from "@/api/structured-analysis.api";
import { webhookApi } from "@/api/webhook.api";
import Dialog from "@/components/primitives/dialog/Dialog";
import { closeModal, openModal } from "@/components/providers/DialogProvider";
import { toaster } from "@/components/providers/ToasterProvider";
import Alert from "@/components/ui/alert/Alert";
import Button from "@/components/ui/button/Button";
import Checkbox from "@/components/ui/checkbox/Checkbox";
import Icon from "@/components/ui/icon/Icon";
import { isApiError } from "@/services/api";
import { $t } from "@/services/i18n";
import { useProjectStore } from "@/stores/project.store";
import { clickable } from "@/utils/a11y";
import { notifyError } from "@/utils/errors";
import { p } from "@/utils/props-builder";

type ResourceType = "agent" | "webhook" | "knowledge" | "structured_analysis" | "secret";

type CopyResult = {
  project_id: string;
  success: boolean;
  error?: string;
};

const props = {
  resourceType: p.$string<ResourceType>(),
  resourceId: p.$string(),
  resourceName: p.$string(),
  sourceProjectId: p.string(), // Project where the resource currently belongs
  resourceData: p.object<Record<string, unknown>>(), // Full resource data (for structured_analysis)
  onSuccess: p.func<() => void>(),
} satisfies ComponentPropsOptions;

type Props = ExtractPublicPropTypes<typeof props>;

/**
 * CopyToProjectsModal - Modal for copying resources to other projects
 *
 * Uses existing GET + POST endpoints to create independent copies.
 * Fetches the resource data, then creates a new one in the target project.
 */
const CopyToProjectsModal = defineComponent({
  name: "CopyToProjectsModal",
  props,
  setup(props) {
    const projectStore = useProjectStore();
    const queryCache = useQueryCache();
    const selectedProjectIds = ref<Set<string>>(new Set());
    const isSubmitting = ref(false);

    // Get available projects (exclude archived and source project of the resource)
    const availableProjects = computed(() => {
      return projectStore.projects
        .filter((p) => p.status !== "archived" && p.id !== props.sourceProjectId)
        .map((p) => ({
          id: p.id,
          name: p.name,
          icon: "lucide:folder",
        }));
    });

    const selectedCount = computed(() => selectedProjectIds.value.size);

    function toggleProject(projectId: string) {
      const newSet = new Set(selectedProjectIds.value);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      selectedProjectIds.value = newSet;
    }

    function isSelected(projectId: string) {
      return selectedProjectIds.value.has(projectId);
    }

    // Copy a single resource to a target project using GET + POST
    async function copyToProject(targetProjectId: string): Promise<CopyResult> {
      try {
        const projectHeader = { headers: { "X-Project-ID": targetProjectId } };

        switch (props.resourceType) {
          case "agent": {
            const agent = await agentsApi.getById(props.resourceId);
            if (!agent) throw new Error("Agent not found");

            // Create copy with all fields except auto-generated ones
            await agentsApi.create(
              {
                name: agent.name,
                description: agent.description,
                instructions: agent.instructions,
                llm_model: agent.llm_model,
                tts_model: agent.tts_model,
                tts_voice: agent.tts_voice,
                language: agent.language,
                keyterm: agent.keyterm,
                tools: agent.tools,
                parameters: agent.parameters,
                share: agent.share,
                post_analysis: agent.post_analysis,
                call_configuration: agent.call_configuration,
                chat_configuration: agent.chat_configuration,
                tts_params: agent.tts_params,
                mcp_servers: agent.mcp_servers,
                project_id: targetProjectId,
              },
              projectHeader
            );
            break;
          }

          case "webhook": {
            await webhookApi.create(
              {
                target_url: props.resourceName, // resourceName is the target_url for webhooks
                events: [], // User will need to configure events
                secret: crypto.randomUUID(), // Generate new secret
                is_active: false, // Start inactive
                retries: 3,
                project_id: targetProjectId,
              },
              projectHeader
            );
            break;
          }

          case "knowledge": {
            const collection = await knowledgeApi.getCollection(props.resourceId);
            const baseName = collection.name;
            let newName = baseName;
            let attempt = 0;

            // Retry with suffix if name already exists (unique per org, not per project yet)
            while (attempt < 10) {
              try {
                await knowledgeApi.createCollection(
                  newName,
                  {
                    description: collection.description || "",
                    model_name: collection.model_name,
                  },
                  projectHeader
                );
                break; // Success, exit loop
              } catch (error) {
                if (isApiError(error) && error.response?.status === 409) {
                  attempt++;
                  newName = `${baseName}_${attempt}`;
                } else {
                  throw error; // Re-throw non-409 errors
                }
              }
            }
            // Note: Documents are NOT copied - user needs to add them separately
            break;
          }

          case "structured_analysis": {
            // Use resourceData passed as prop (no GET endpoint exists)
            const data = props.resourceData as StructuredAnalysis;
            if (!data) throw new Error("Resource data not provided");

            await structuredAnalysisApi.create(
              {
                name: data.name,
                description: data.description,
                model: data.model,
                schema: data.schema,
                status: data.status,
                metadata: data.metadata,
                project_id: targetProjectId,
              },
              projectHeader
            );
            break;
          }

          case "secret": {
            await secretsApi.clone(props.resourceId, targetProjectId);
            break;
          }
        }

        return { project_id: targetProjectId, success: true };
      } catch (error) {
        return {
          project_id: targetProjectId,
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    }

    async function handleSubmit() {
      if (selectedProjectIds.value.size === 0) {
        toaster.error($t("copy_modal.error_no_selection"));
        return;
      }

      isSubmitting.value = true;
      try {
        const projectIds = Array.from(selectedProjectIds.value);

        // Copy to each selected project
        const results = await Promise.all(projectIds.map((pid) => copyToProject(pid)));

        const successCount = results.filter((r) => r.success).length;
        const failedCount = results.filter((r) => !r.success).length;

        if (failedCount === 0) {
          toaster.success($t("copy_modal.success", { count: successCount }));
        } else if (successCount > 0) {
          toaster.warning(
            $t("copy_modal.partial_success", { success: successCount, failed: failedCount })
          );
        } else {
          const firstError = results.find((r) => r.error)?.error ?? "Unknown error";
          toaster.error(firstError);
        }

        // Invalidate caches to refresh lists
        if (props.resourceType === "webhook") {
          queryCache.invalidateQueries({ key: ["webhooks"] as any });
        } else if (props.resourceType === "agent") {
          queryCache.invalidateQueries({ key: ["agents"] as any });
        } else if (props.resourceType === "knowledge") {
          queryCache.invalidateQueries({ key: ["knowledge"] as any });
        } else if (props.resourceType === "structured_analysis") {
          queryCache.invalidateQueries({ key: ["structured-analyses"] as any });
        } else if (props.resourceType === "secret") {
          queryCache.invalidateQueries({ key: ["secrets"] as any });
        }

        props.onSuccess?.();
        closeModal();
      } catch (error) {
        notifyError(error);
      } finally {
        isSubmitting.value = false;
      }
    }

    return () => (
      <>
        <Dialog.Header>
          <Dialog.Title>
            <div class="flex items-center gap-2">
              <Icon icon="lucide:copy" class="h-5 w-5" />
              {$t("copy_modal.title")}
            </div>
          </Dialog.Title>
          <Dialog.Description>{$t("copy_modal.description")}</Dialog.Description>
        </Dialog.Header>

        <Dialog.Body>
          <div class="space-y-4">
            {/* Resource being copied */}
            <div class="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2">
              <span class="text-sm text-muted-foreground">{$t("copy_modal.resource_label")}:</span>
              <span class="text-sm font-medium">{props.resourceName}</span>
            </div>

            {/* Warning for agents */}
            {props.resourceType === "agent" && (
              <Alert type="warning" size="sm" title={$t("copy_modal.agent_warning")} />
            )}

            {/* Project selection */}
            {availableProjects.value.length === 0 ? (
              <div class="py-4 text-center text-sm text-muted-foreground">
                {$t("copy_modal.no_projects_available")}
              </div>
            ) : (
              <div class="max-h-64 space-y-2 overflow-y-auto">
                {availableProjects.value.map((project) => (
                  <div
                    key={project.id}
                    class="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 transition-colors hover:bg-muted/50"
                    {...clickable(() => toggleProject(project.id))}
                  >
                    <Checkbox
                      modelValue={isSelected(project.id)}
                      onUpdate:modelValue={() => toggleProject(project.id)}
                    />
                    <Icon icon={project.icon} class="h-4 w-4 text-muted-foreground" />
                    <span class="text-sm">{project.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Dialog.Body>

        <Dialog.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {$t("cancel")}
          </Button>
          <Button
            color="brand"
            onClick={handleSubmit}
            loading={isSubmitting.value}
            disabled={selectedCount.value === 0 || availableProjects.value.length === 0}
          >
            {selectedCount.value > 0
              ? $t("copy_modal.copy_button", { count: selectedCount.value })
              : $t("copy_to_projects")}
          </Button>
        </Dialog.Footer>
      </>
    );
  },
});

export function openCopyToProjectsModal(modalProps: Props) {
  openModal(<CopyToProjectsModal {...modalProps} />, { size: "sm" });
}

export default CopyToProjectsModal;
